import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  BookOpen,
  GraduationCap,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  BarChart3,
  MessageSquare,
  Award,
  Target,
  TrendingUp,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const TrainerStudentProfile = ({ onMenuClick }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchStudentData = useCallback(async () => {
    try {
      const response = await api.get(`/trainer/students/${studentId}/profile`);
      setStudent(response.data);
    } catch (error) {
      console.error('Error fetching student profile:', error);
      toast.error('Erreur lors du chargement du profil');
      setStudent(null);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchStudentData();
  }, [fetchStudentData]);

  const handleStartChat = () => {
    navigate('/messages');
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'submission': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'quiz': return <GraduationCap className="w-4 h-4 text-violet-500" />;
      case 'lesson': return <BookOpen className="w-4 h-4 text-emerald-500" />;
      default: return <Activity className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-slate-500">Étudiant non trouvé</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Profil Étudiant" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        {/* Student Header Card */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold">
                {student.user.first_name[0]}{student.user.last_name[0]}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {student.user.first_name} {student.user.last_name}
                  </h1>
                  <Badge className={student.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                    {student.status === 'active' ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {student.user.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {student.phone}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Inscrit le {new Date(student.enrollment_date).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-2">N° Étudiant: {student.student_number}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Button onClick={handleStartChat}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Envoyer un message
                </Button>
                <Button variant="outline">
                  <Award className="w-4 h-4 mr-2" />
                  Voir les certificats
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-5 h-5 text-violet-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Progression</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{student.progress.overall}%</p>
              <Progress value={student.progress.overall} className="mt-2 h-2" />
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Moyenne</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{student.performance.avg_grade.toFixed(1)}/20</p>
              <p className="text-xs text-emerald-600 mt-1">#{student.performance.rank} sur {student.performance.total_students}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Taux de réussite</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{student.performance.success_rate}%</p>
              <p className="text-xs text-blue-600 mt-1">{student.performance.quizzes_passed}/{student.performance.quizzes_total} quiz réussis</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Temps d'étude</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{student.progress.time_spent}</p>
              <p className="text-xs text-amber-600 mt-1">Dernière activité: {new Date(student.progress.last_activity).toLocaleDateString('fr-FR')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 grid w-full max-w-xl grid-cols-4">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="progress">Progression</TabsTrigger>
            <TabsTrigger value="assignments">Devoirs</TabsTrigger>
            <TabsTrigger value="quizzes">Évaluations</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Learning Progress */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-violet-600" />
                    Progression d'Apprentissage
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600 dark:text-slate-400">Modules complétés</span>
                      <span className="font-medium">{student.progress.modules_completed}/{student.progress.modules_total}</span>
                    </div>
                    <Progress value={(student.progress.modules_completed / student.progress.modules_total) * 100} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600 dark:text-slate-400">Leçons terminées</span>
                      <span className="font-medium">{student.progress.lessons_completed}/{student.progress.lessons_total}</span>
                    </div>
                    <Progress value={(student.progress.lessons_completed / student.progress.lessons_total) * 100} className="h-2" />
                  </div>
                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-3">Cours suivis</h4>
                    {student.courses.map(course => (
                      <div key={course.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                        <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-violet-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{course.name}</p>
                          <Progress value={course.progress} className="h-1.5 mt-1" />
                        </div>
                        <Badge variant="outline">{course.grade}/20</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-violet-600" />
                    Activité Récente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {student.activity.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                          {getActivityIcon(item.type)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-900 dark:text-white">{item.description}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(item.date).toLocaleDateString('fr-FR', { 
                              day: 'numeric', 
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {item.score !== undefined && (
                          <Badge className={item.score >= 50 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {item.score}%
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress">
            <Card>
              <CardHeader>
                <CardTitle>Détail de la Progression par Cours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {student.courses.map(course => (
                    <div key={course.id} className="p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                            {course.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900 dark:text-white">{course.name}</h3>
                            <p className="text-sm text-slate-500">Progression: {course.progress}%</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-lg px-3 py-1">{course.grade}/20</Badge>
                      </div>
                      <Progress value={course.progress} className="h-3" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <Card>
              <CardHeader>
                <CardTitle>Devoirs ({student.assignments.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {student.assignments.map(assignment => (
                    <div key={assignment.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        assignment.status === 'graded' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                        assignment.status === 'submitted' ? 'bg-blue-100 dark:bg-blue-900/30' :
                        'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {assignment.status === 'graded' ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        ) : assignment.status === 'submitted' ? (
                          <Clock className="w-5 h-5 text-blue-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-white">{assignment.title}</p>
                        <p className="text-xs text-slate-500">
                          {assignment.submitted_at ? `Soumis le ${new Date(assignment.submitted_at).toLocaleDateString('fr-FR')}` : `Date limite: ${new Date(assignment.due_date).toLocaleDateString('fr-FR')}`}
                        </p>
                      </div>
                      <div>
                        {assignment.grade !== null ? (
                          <Badge className={assignment.grade >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {assignment.grade}/20
                          </Badge>
                        ) : assignment.status === 'submitted' ? (
                          <Button size="sm">Corriger</Button>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">En retard</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quizzes Tab */}
          <TabsContent value="quizzes">
            <Card>
              <CardHeader>
                <CardTitle>Résultats des Évaluations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {student.quizzes.map(quiz => (
                    <div key={quiz.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        quiz.passed ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {quiz.passed ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-white">{quiz.title}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(quiz.date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-900 dark:text-white">{quiz.score}%</p>
                          <p className="text-xs text-slate-500">{quiz.score}/{quiz.max_score}</p>
                        </div>
                        <Badge className={quiz.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {quiz.passed ? 'Réussi' : 'Échoué'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default TrainerStudentProfile;
